import { DeleteIcon, EditIcon, InfiniteTable } from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import {
  useTaxCouponList,
  useTaxCouponRemove,
} from '@/core/domain/coupon/coupons/coupons.hooks';
import {
  TaxCoupon,
  TaxCouponListRequest,
  TaxCouponStatus,
  TaxCouponStatusColor,
  TaxCouponStatusLabel,
} from '@/core/domain/coupon/coupons/coupons.types';
import { useDebounce } from '@/hooks';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { COLUMNS_WIDTH } from '@/utils/table';
import { Space, Divider, Modal } from 'antd';

interface Props {
  queryParams?: TaxCouponListRequest;
}

export default function CouponList({ queryParams }: Props) {
  const debounce = useDebounce(queryParams);

  const couponRemove = useTaxCouponRemove();

  const couponList = useTaxCouponList({
    pageSize: 10,
    ...debounce,
  });

  const coupons = flatInfiniteData(couponList.data);

  function confirmRemove(coupon: TaxCoupon) {
    const origins = coupon.origins?.map((origin) => origin.entityCode);

    Modal.confirm({
      title: 'Remoção de cupom fiscal (SAT)',
      content: (
        <>
          <p>
            {`Você está prestes a remover o registro do cupom fiscal relacionado
            ${
              (origins?.length || 0) > 1 ? 'aos orçamentos' : 'ao orçamento'
            } ${origins?.join(', ')}`}
          </p>
          <p>Tem certeza que quer executar esta ação?</p>
        </>
      ),
      okText: 'Remover',
      okType: 'danger',
      onOk: () => couponRemove.mutateAsync(coupon.id),
    });
  }

  return (
    <>
      <InfiniteTable
        dataSource={coupons}
        rowKey={(row) => row.id}
        hasNextPage={couponList.hasNextPage}
        fetchNextPage={couponList.fetchNextPage}
        isFetching={couponList.isFetching}
        loading={couponList.isLoading}
        columns={[
          {
            title: 'Número',
            dataIndex: ['sefazBreakdown', 'cfeNumber'],
            width: COLUMNS_WIDTH.default,
            ellipsis: true,
            render: (value) => value || '-',
          },
          {
            title: 'Série',
            dataIndex: ['sefazBreakdown', 'serie'],
            width: COLUMNS_WIDTH.default,
            ellipsis: true,
            render: (value) => value || '-',
          },
          {
            title: 'Dt. Emissão',
            dataIndex: ['sefazBreakdown', 'emissionDate'],
            width: COLUMNS_WIDTH.date,
            ellipsis: true,
            render: (value) => value || '-',
          },
          {
            title: 'Destinatário',
            dataIndex: ['recipient', 'clientName'],
            render: (value) => value || '-',
            width: COLUMNS_WIDTH.default,
            ellipsis: true,
          },
          {
            title: 'Situação',
            dataIndex: ['status'],
            width: COLUMNS_WIDTH.default,
            render: (value: TaxCouponStatus) => (
              <StatusTag
                color={TaxCouponStatusColor[value]}
                label={TaxCouponStatusLabel[value]}
              />
            ),
          },
          {
            title: 'Ação',
            width: 100,
            fixed: 'right',
            align: 'right',
            render: (_, item) => (
              <Space>
                <EditIcon
                  disabled={
                    !hasPermissions([Authorities.FISCAL_COUPON_VIEW_DETAIL])
                  }
                  redirect={`/app/fiscal/cfe/${item.id}/${queryParams?.companyId}/general`}
                />

                <Divider type="vertical" />

                <DeleteIcon
                  disabled={
                    item.status !== TaxCouponStatus.TYPING ||
                    !hasPermissions([Authorities.FISCAL_COUPON_REMOVE])
                  }
                  onClick={() => confirmRemove(item)}
                />
              </Space>
            ),
          },
        ]}
      />
    </>
  );
}
